import React, { useEffect, useState, useMemo } from "react";
import { InputText } from "primereact/inputtext";
import { InputSwitch } from "primereact/inputswitch";
import { Dropdown } from "primereact/dropdown";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Dialog } from "primereact/dialog";
import { InputNumber } from "primereact/inputnumber";
import { Tag } from "primereact/tag";
import {
  Save, Settings, CreditCard, Plus, Trash2,
  Pencil, Search, FileText, RefreshCw, Globe, Wallet, Smartphone, Briefcase, Package, ShieldCheck, Mail, Building2, Upload, MonitorSmartphone, Settings2, Shield, Eye, EyeOff, LayoutDashboard, Database, KeyRound, Phone, Lock, Landmark, Link as LinkIcon, Download, Clock, Image as ImageIcon, Zap, CheckCircle2, History, X, Truck
} from "lucide-react";
import { IconTextButton } from "../../components/ButtonComponents";
import { SidebarTabs } from "../../components/SidebarTabs";
import { CustomSelectInput } from "../../components/CustomSelectInput";
import { toast } from "react-toastify";

// Store & Hook Importları
import { useSettingsStore } from "../../store/settingsStore";
import { useFirmSettingsStore } from "../../store/firmSettingsStore";
import { useSettingDefinitionStore } from "../../store/settingDefinitionStore";
import { useSelectDialog } from "../../hooks/useSelectDialog";
import { useBankStore } from "../../store/bankStore";
import { ConfirmModal } from "../../components/ConfirmModal";
import { PoolCalculationResult } from "../../components/PoolCalculationResult";
import { Bank } from "../../types/bank";
import { Filter } from "../../types";

const SettingRow = ({ title, description, checked, onChange }: any) => (
  <div className="group flex items-center justify-between p-4 rounded-xl bg-card border border-borderColor hover:border-primary hover:shadow-md transition-all duration-300 hover:bg-bgAlt cursor-pointer" onClick={() => onChange({ value: !checked })}>
    <div className="flex flex-col gap-0.5">
      <span className="text-[13px] font-bold text-text opacity-90 group-hover:text-primary transition-colors">{title}</span>
      <span className="text-[11px] text-text opacity-60 leading-tight">{description}</span>
    </div>
    <div className="pl-2 shrink-0" onClick={(e) => e.stopPropagation()}>
      <InputSwitch checked={checked} onChange={onChange} />
    </div>
  </div>
);

const SettingSection = ({ title, icon: Icon, colorClass, children }: any) => (
  <div className="flex flex-col shadow-sm  card p-0 overflow-hidden">
    <div className="card2 shadow-none border-none p-2 flex items-center gap-3   ">
      {Icon && (
        <div className={`p-2 rounded-lg  ${colorClass}`}>
          <Icon size={18} />
        </div>
      )}
      <h3 className="font-bold text-text">{title}</h3>
    </div>
    <div className="  p-2  space-y-3     ">
      {children}
    </div>
  </div>
);

export const FirmSettingsPage: React.FC = () => {
  // Store Stateleri
  const {
    loading, modules,
    fetchSettings
  } = useSettingsStore();

  // Yerel UI Stateleri
  const [activeTab, setActiveTab] = useState(0);

  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  // Banka Seçimi
  const { fetchDataDynamic: fetchBanks, loading: bankLoading } = useBankStore();
  const bankSelect = useSelectDialog<Bank>();

  const { fetchSettings: fetchFirmSettings, saveSettings: saveFirmSettings, triggerPoolCalculation } = useFirmSettingsStore();
  const [localFirmSettings, setLocalFirmSettings] = useState<any>({});
  const [definitionTree, setDefinitionTree] = useState<any[]>([]);

  const [isCalculatingPool, setIsCalculatingPool] = useState<boolean>(false);
  const [poolCalculationResult, setPoolCalculationResult] = useState<any>(null);

  const [poolTargetMonth, setPoolTargetMonth] = useState<number>(new Date().getMonth() === 0 ? 11 : new Date().getMonth() - 1);
  const [poolTargetYear, setPoolTargetYear] = useState<number>(new Date().getMonth() === 0 ? new Date().getFullYear() - 1 : new Date().getFullYear());

  const monthOptions = [
    { label: "Ocak", value: 0 }, { label: "Şubat", value: 1 }, { label: "Mart", value: 2 },
    { label: "Nisan", value: 3 }, { label: "Mayıs", value: 4 }, { label: "Haziran", value: 5 },
    { label: "Temmuz", value: 6 }, { label: "Ağustos", value: 7 }, { label: "Eylül", value: 8 },
    { label: "Ekim", value: 9 }, { label: "Kasım", value: 10 }, { label: "Aralık", value: 11 }
  ];

  const yearOptions = Array.from({ length: 5 }, (_, i) => {
    const y = new Date().getFullYear() - i;
    return { label: y.toString(), value: y };
  });

  useEffect(() => {
    fetchSettings();
    useSettingDefinitionStore.fetchTree().then(setDefinitionTree);
    fetchFirmSettings().then(res => {
      if (res) {
        setLocalFirmSettings({
          ...res,
          customerAppSettings: res.customerAppSettings || {},
          firmAppSettings: res.firmAppSettings || {}
        });
      }
    });
  }, [fetchSettings, fetchFirmSettings]);

  const tabs = useMemo(() => {
    if (!modules) return [];
    const _tabs = [];

    const getTabIcon = (key: string) => {
      switch (key) {
        case 'general': return Wallet;
        case 'customer_app': return Smartphone;
        case 'sales_app': return Briefcase;
        case 'wms_app': return Package;
        case 'crm_app': return ShieldCheck;
        case 'textile_settings': return Package;
        default: return Settings;
      }
    };

    // Dinamik sekmeleri oluştur
    definitionTree.forEach(tabData => {
      const TabIcon = getTabIcon(tabData.tabKey);

      _tabs.push({
        id: tabData.tabKey,
        mobileTitle: tabData.tabLabel || tabData.tabKey,
        onSave: async () => {
          const success = await saveFirmSettings(localFirmSettings);
          if (success) toast.success(`${tabData.tabLabel || tabData.tabKey} ayarları kaydedildi.`);
        },
        header: <><TabIcon size={16} className="mr-2" /> {tabData.tabLabel || tabData.tabKey}</>,
        content: (
          <div className="p-4 space-y-6 mx-auto">
            {/* Responsive Action Bar */}
            <div className="hidden md:flex items-center justify-between sticky top-0 z-50 bg-bgAlt backdrop-blur-md -mx-4 -mt-4 p-3 border-b border-borderColor shadow-sm mb-6 rounded-t-lg">
              <div className="flex flex-col">
                <h3 className="font-bold text-sm text-text hidden md:block">{tabData.tabLabel || tabData.tabKey} Ayarları</h3>
                <p className="text-[11px] text-text opacity-60 hidden md:block">{tabData.tabLabel || tabData.tabKey} ile ilgili ayarlarınızı yönetin.</p>
                <h3 className="font-bold text-sm text-text md:hidden">{tabData.tabLabel || tabData.tabKey}</h3>
              </div>
              <IconTextButton icon={Save} className="btn-new shadow-md whitespace-nowrap" onClick={async () => {
                const success = await saveFirmSettings(localFirmSettings);
                if (success) toast.success(`${tabData.tabLabel || tabData.tabKey} ayarları kaydedildi.`);
              }}>Kaydet</IconTextButton>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-2 gap-6">
              {tabData.sections.map((section: any) => (
                <SettingSection key={section.sectionKey} title={section.sectionLabel || section.sectionKey} icon={Settings} colorClass="card text-primary">
                  {section.fields.map((field: any) => {
                    const value = field.targetJson
                      ? localFirmSettings?.[field.targetJson]?.[field.fieldKey]
                      : localFirmSettings?.[field.fieldKey];

                    const handleChange = (e: any) => {
                      let newValue = e.value !== undefined ? e.value : e.target.value;
                      if (field.fieldType === 'NUMBER') {
                        newValue = parseFloat(newValue) || 0;
                      }

                      if (field.targetJson) {
                        setLocalFirmSettings({
                          ...localFirmSettings,
                          [field.targetJson]: {
                            ...(localFirmSettings[field.targetJson] || {}),
                            [field.fieldKey]: newValue
                          }
                        });
                      } else {
                        setLocalFirmSettings({
                          ...localFirmSettings,
                          [field.fieldKey]: newValue
                        });
                      }
                    };

                    if (field.fieldType === 'BOOLEAN') {
                      return (
                        <SettingRow
                          key={field.fieldKey}
                          title={field.label}
                          description={field.description}
                          checked={value || false}
                          onChange={handleChange}
                        />
                      );
                    } else if (field.fieldType === 'NUMBER') {
                      return (
                        <div key={field.fieldKey} className="flex flex-col gap-2 p-3 bg-bgAlt border border-borderColor rounded-lg mt-2">
                          <label className="text-[10px] font-bold uppercase opacity-60">{field.label}</label>
                          <InputText
                            type="number"
                            step="0.1"
                            value={value?.toString() || ""}
                            onChange={(e) => handleChange({ target: { value: e.target.value } })}
                            placeholder={field.description || ""}
                            className="p-inputtext-sm w-full"
                          />
                        </div>
                      );
                    } else if (field.fieldType === 'STRING') {
                      return (
                        <div key={field.fieldKey} className="flex flex-col gap-2 p-3 bg-bgAlt border border-borderColor rounded-lg mt-2">
                          <label className="text-[10px] font-bold uppercase opacity-60">{field.label}</label>
                          <InputText
                            type="text"
                            value={value || ""}
                            onChange={(e) => handleChange({ target: { value: e.target.value } })}
                            placeholder={field.description || ""}
                            className="p-inputtext-sm w-full"
                          />
                        </div>
                      );
                    } else if (field.fieldType === 'SELECT') {
                      return (
                        <div key={field.fieldKey} className="flex flex-col gap-2 p-3 bg-bgAlt border border-borderColor rounded-lg mt-2">
                          <div className="flex flex-col">
                            <label className="text-[10px] font-bold uppercase opacity-60">{field.label}</label>
                            {field.description && <span className="text-[10px] opacity-50 mt-0.5">{field.description}</span>}
                          </div>
                          <Dropdown
                            value={value || field.options?.[0]?.value}
                            options={field.options || []}
                            onChange={(e) => handleChange({ target: { value: e.value } })}
                            optionLabel="label"
                            optionValue="value"
                            placeholder="Seçiniz"
                            className="p-dropdown-sm w-full"
                          />
                        </div>
                      );
                    }
                    return null;
                  })}
                </SettingSection>
              ))}
            </div>
          </div>
        )
      });
    });

    // --- İSKONTO HAVUZU SEKİMESİ ---
    _tabs.push({
      id: 'discount_pool',
      mobileTitle: 'İskonto Havuzu',
      onSave: async () => {
        const success = await saveFirmSettings(localFirmSettings);
        if (success) toast.success(`İskonto Havuzu ayarları kaydedildi.`);
      },
      header: <><Wallet size={16} className="mr-2" /> İskonto Havuzu</>,
      content: (
        <div className="p-4 space-y-6 mx-auto">
          <div className="hidden md:flex items-center justify-between sticky top-0 z-50 bg-bgAlt backdrop-blur-md -mx-4 -mt-4 p-3 border-b border-borderColor shadow-sm mb-6 rounded-t-lg">
            <div className="flex flex-col">
              <h3 className="font-bold text-sm text-text hidden md:block">İskonto Havuzu Ayarları</h3>
              <p className="text-[11px] text-text opacity-60 hidden md:block">Grup A (normal, sabit yok): max %12 | Grup B (normal, sabit var): max %18 toplam | Grup C (bayi): sabit + max %7 ek havuz</p>
              <h3 className="font-bold text-sm text-text md:hidden">İskonto Havuzu</h3>
            </div>
            <IconTextButton icon={Save} className="btn-new shadow-md whitespace-nowrap" onClick={async () => {
              const success = await saveFirmSettings(localFirmSettings);
              if (success) toast.success(`İskonto Havuzu ayarları kaydedildi.`);
            }}>Kaydet</IconTextButton>
          </div>

          <div className="grid grid-cols-1 gap-6 pb-20">
            <SettingSection title="Plasiyer İskonto Havuzu" icon={Wallet} colorClass="card text-primary">
              <div className="space-y-4 p-2">
                <div className="flex items-center justify-between p-3 border rounded-lg opacity-90 border-borderColor hover:border-primary transition-all">
                  <div className="flex flex-col">
                    <span className="text-sm font-bold">Havuz Sistemi Aktif</span>
                    <small className="text-[10px] opacity-60">Aktif edilirse plasiyerler tahsilatlarından iskonto havuzu bakiyesi kazanır.</small>
                  </div>
                  <InputSwitch
                    checked={localFirmSettings.enableSalesDiscountPool || false}
                    onChange={(e) => setLocalFirmSettings({ ...localFirmSettings, enableSalesDiscountPool: e.value })}
                  />
                </div>

                <div className="flex flex-col md:flex-row items-center justify-between p-3 border rounded-lg bg-bgAlt border-borderColor gap-4">
                  <div className="flex flex-col">
                    <span className="text-sm font-bold text-primary">Geçmiş Dönem Hesaplaması</span>
                    <small className="text-[10px] opacity-70">Belirtilen ayın tahsilatlarına göre havuz bakiyelerini oluştur.</small>
                  </div>
                  <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 mt-3 w-full">
                    <div className="flex-1 flex gap-2 w-full">
                      <Dropdown
                        value={poolTargetMonth}
                        options={monthOptions}
                        onChange={(e) => setPoolTargetMonth(e.value)}
                        className="p-dropdown-sm flex-1"
                      />
                      <Dropdown
                        value={poolTargetYear}
                        options={yearOptions}
                        onChange={(e) => setPoolTargetYear(e.value)}
                        className="p-dropdown-sm flex-1"
                      />
                    </div>
                    <IconTextButton
                      onClick={() => setIsConfirmOpen(true)}
                      icon={RefreshCw}
                      loading={isCalculatingPool}
                      className="bg-danger text-white hover:bg-red-700 whitespace-nowrap w-full sm:w-auto flex justify-center py-1.5"
                    >
                      {isCalculatingPool ? "Hesaplanıyor..." : "Yeniden Hesapla"}
                    </IconTextButton>
                  </div>
                </div>

                {/* Havuz Hesaplama Sonucu */}
                {poolCalculationResult && (
                  <PoolCalculationResult
                    result={poolCalculationResult}
                    periodMonth={poolTargetMonth}
                    periodYear={poolTargetYear}
                    firmId={localFirmSettings.firmId || localFirmSettings.id}
                    onClose={() => setPoolCalculationResult(null)}
                  />
                )}

                {localFirmSettings.enableSalesDiscountPool && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-3 bg-bgAlt rounded-lg border border-borderColor">
                    <div className="flex flex-col gap-2">
                      <label className="text-[10px] font-bold uppercase opacity-60">Havuz Kazanım Oranı (%)</label>
                      <InputText
                        type="number"
                        step="0.1"
                        value={localFirmSettings.discountPoolEarnRate?.toString() || ""}
                        onChange={(e) => setLocalFirmSettings({ ...localFirmSettings, discountPoolEarnRate: parseFloat(e.target.value) || 0 })}
                        placeholder="Örn: 1.0"
                        className="p-inputtext-sm w-full"
                      />
                    </div>
                    <div className="flex flex-col gap-2">
                      <label className="text-[10px] font-bold uppercase opacity-60">Maksimum İskonto Kullanımı (%)</label>
                      <InputText
                        type="number"
                        step="0.1"
                        value={localFirmSettings.maxItemPoolDiscountRate?.toString() || ""}
                        onChange={(e) => setLocalFirmSettings({ ...localFirmSettings, maxItemPoolDiscountRate: parseFloat(e.target.value) || 0 })}
                        placeholder="Örn: 5.0"
                        className="p-inputtext-sm w-full"
                      />
                    </div>

                    <div className="flex flex-col gap-2">
                      <label className="text-[10px] font-bold uppercase opacity-60">Bayi Tahsilat Kazanım Oranı (%)</label>
                      <InputNumber
                        value={localFirmSettings.poolEarnRateDealer}
                        onValueChange={(e) => setLocalFirmSettings({ ...localFirmSettings, poolEarnRateDealer: e.value ?? 0 })}
                        minFractionDigits={1} maxFractionDigits={2}
                        className="p-inputtext-sm w-full"
                        placeholder="Örn: 2.0"
                      />
                    </div>
                    <div className="flex flex-col gap-2">
                      <label className="text-[10px] font-bold uppercase opacity-60">Standart Tahsilat Kazanım Oranı (%)</label>
                      <InputNumber
                        value={localFirmSettings.poolEarnRateNormal}
                        onValueChange={(e) => setLocalFirmSettings({ ...localFirmSettings, poolEarnRateNormal: e.value ?? 0 })}
                        minFractionDigits={1} maxFractionDigits={2}
                        className="p-inputtext-sm w-full"
                        placeholder="Örn: 1.0"
                      />
                    </div>

                    <div className="flex flex-col gap-2">
                      <label className="text-[10px] font-bold uppercase opacity-60">Normal Cari + Sabit — Toplam Maks. (%)</label>
                      <InputNumber
                        value={localFirmSettings.maxDiscountNormalWithFixed}
                        onValueChange={(e) => setLocalFirmSettings({ ...localFirmSettings, maxDiscountNormalWithFixed: e.value ?? 0 })}
                        minFractionDigits={1} maxFractionDigits={2}
                        className="p-inputtext-sm w-full"
                        placeholder="Örn: 5.0"
                      />
                    </div>
                    <div className="flex flex-col gap-2">
                      <label className="text-[10px] font-bold uppercase opacity-60">Normal Cari (Sabit Yok) — Maks. (%)</label>
                      <InputNumber
                        value={localFirmSettings.maxDiscountNormalNoFixed}
                        onValueChange={(e) => setLocalFirmSettings({ ...localFirmSettings, maxDiscountNormalNoFixed: e.value ?? 0 })}
                        minFractionDigits={1} maxFractionDigits={2}
                        className="p-inputtext-sm w-full"
                        placeholder="Örn: 10.0"
                      />
                    </div>

                    <div className="flex flex-col gap-2 md:col-span-2">
                      <label className="text-[10px] font-bold uppercase opacity-60">Bayi Maks. Ek Havuz İskontosu (%)</label>
                      <small className="text-[10px] opacity-60">Bayiler sabit iskontodan bağımsız olarak bu orana kadar ek havuz iskontosu uygulayabilir.</small>
                      <InputNumber
                        value={(localFirmSettings as any).maxDiscountDealer ?? 7}
                        onValueChange={(e) => setLocalFirmSettings({ ...localFirmSettings, maxDiscountDealer: e.value ?? 7 } as any)}
                        minFractionDigits={1} maxFractionDigits={2}
                        className="p-inputtext-sm w-full"
                        placeholder="Örn: 7.0"
                      />
                    </div>
                  </div>
                )}
              </div>
            </SettingSection>
          </div>
        </div>
      )
    });


    return _tabs;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modules, localFirmSettings, definitionTree, poolCalculationResult, isCalculatingPool, poolTargetMonth, poolTargetYear]);

  return (<>
    <ConfirmModal
      isOpen={isConfirmOpen}
      title="Onay"
      message={`${monthOptions.find(m => m.value === poolTargetMonth)?.label} ${poolTargetYear} dönemine ait havuz bakiyelerini yeniden hesaplamak istediğinize emin misiniz?`}
      onConfirm={async () => {
        setIsCalculatingPool(true);
        try {
          const res = await triggerPoolCalculation(localFirmSettings.firmId || localFirmSettings.id, poolTargetMonth, poolTargetYear);
          if (res.success) {
            toast.success(res.message || "Havuz hesaplaması başarıyla tamamlandı.");
            setPoolCalculationResult(res);
          } else {
            toast.error(res.message || "Havuz hesaplanırken hata oluştu.");
          }
        } catch (err) {
          console.error("Pool calculation error:", err);
          toast.error("Havuz hesaplanırken bir hata oluştu.");
        } finally {
          setIsCalculatingPool(false);
          setIsConfirmOpen(false);
        }
      }}
      onCancel={() => setIsConfirmOpen(false)}
    />
    <div className="p-4 space-y-4 pb-24 md:pb-4">
      <SidebarTabs tabs={tabs} onTabChange={(idx) => setActiveTab(idx)} initialActiveIndex={0} />

      {/* Mobile Sticky Bottom Bar (Rendered outside framer-motion to avoid transform issues) */}
      {tabs[activeTab] && tabs[activeTab].onSave && (
        <div className="md:hidden fixed bottom-0 left-0 right-0 z-[100] bg-card border-t border-borderColor shadow-[0_-10px_20px_-10px_rgba(0,0,0,0.1)] p-4 flex items-center justify-between pb-safe">
          <div className="flex flex-col">
            <h3 className="font-bold text-[15px] text-text font-bold">{tabs[activeTab].mobileTitle}</h3>
          </div>
          <IconTextButton
            icon={Save}
            className="btn-new shadow-md whitespace-nowrap"
            onClick={tabs[activeTab].onSave}
          >
            Kaydet
          </IconTextButton>
        </div>
      )}
    </div>
  </>
  );
};